/**
 * Invitations service (recipient side) — MOCK (CT-5575 / CT-5567).
 *
 * The invitee opens an emailed link with a token. The public lookup tells the screens what to
 * render; accepting requires an authenticated session (no access before accept). No backend
 * contract exists yet (ARP-1433 / ARP-1480): module-level seed, platform envelope
 * `{ status_code, body, meta }`, snake_case bodies. Existing IMS core invitation lambdas are
 * to be verified in a spike, not rebuilt.
 */
import { envelope, errorEnvelope, notFoundEnvelope } from '../shared/utils/mockServiceHelpers.js';
import { isInvitationExpired } from '../shared/utils/invitationExpiry.js';
import { setMemberStatus } from './membersService.js';

const DAY = 86_400_000;

/** Status values of the (mock) invitation API. */
export const API_INVITATION_STATUS = Object.freeze({
  PENDING: 'pending',
  EXPIRED: 'expired',
  REVOKED: 'revoked',
  ACCEPTED: 'accepted',
});

/** Tokens the mock knows; screens and tests open these links. */
export const INVITATION_TOKENS = Object.freeze({
  valid: 'valid-token',
  noAccess: 'no-access-token',
  expired: 'expired-token',
  stale: 'stale-token',
  revoked: 'revoked-token',
  used: 'used-token',
});

const STUDIOS = {
  governance: { id: 'governance', name: 'Governance Studio', path: '/governance' },
  data: { id: 'data', name: 'Data Studio', path: '/data-studio' },
  agentic: { id: 'agentic', name: 'Agentic Studio', path: '/agentic-studio' },
};

const buildSeed = () => {
  const now = Date.now();
  const base = {
    workspace_name: 'Avance Financial',
    invited_email: 'maria@avance.com',
    welcome_message: null,
    expires_at: new Date(now + 6 * DAY).toISOString(),
  };
  return {
    [INVITATION_TOKENS.valid]: {
      ...base,
      status: API_INVITATION_STATUS.PENDING,
      welcome_message: 'Welcome aboard! We are glad to have you on the team.',
      studio_ids: ['data', 'agentic'],
    },
    [INVITATION_TOKENS.noAccess]: { ...base, status: API_INVITATION_STATUS.PENDING, studio_ids: [] },
    [INVITATION_TOKENS.expired]: {
      ...base,
      status: API_INVITATION_STATUS.EXPIRED,
      expires_at: new Date(now - DAY).toISOString(),
      studio_ids: ['data'],
    },
    [INVITATION_TOKENS.stale]: {
      ...base,
      status: API_INVITATION_STATUS.PENDING,
      expires_at: new Date(now - 2 * DAY).toISOString(),
      studio_ids: ['data'],
    },
    [INVITATION_TOKENS.revoked]: { ...base, status: API_INVITATION_STATUS.REVOKED, studio_ids: ['data'] },
    [INVITATION_TOKENS.used]: { ...base, status: API_INVITATION_STATUS.ACCEPTED, studio_ids: ['data'] },
  };
};

let invitations = buildSeed();

/** Test-only: restore the seeded invitations. */
export const __resetMockInvitationsForTests = () => {
  invitations = buildSeed();
};

const publicView = ({ studio_ids: _studios, ...invitation }) => invitation;

/**
 * Public details of an invitation, for the accept / status screens. No session needed.
 * TODO(CT-5575 / backend): `GET /people-access/invitations/:token` — contract TBD (ARP/backend).
 */
export const getInvitationByToken = (token) => {
  const invitation = invitations[token];
  return Promise.resolve(
    invitation ? envelope(publicView(invitation)) : notFoundEnvelope('Invitation not found')
  );
};

/**
 * Accept an invitation. Requires an authenticated session: credentials alone never grant access.
 * Returns where the user lands first (the first studio they can open, or none).
 * TODO(CT-5567 / backend): `POST /people-access/invitations/:token/accept` — contract TBD (ARP/backend).
 */
export const acceptInvitation = (token, { authenticated = false } = {}) => {
  const invitation = invitations[token];
  if (!invitation) return Promise.resolve(notFoundEnvelope('Invitation not found'));
  if (!authenticated) {
    return Promise.resolve(
      errorEnvelope(401, 'AUTH_REQUIRED', 'Sign in before accepting the invitation.')
    );
  }
  if (invitation.status !== API_INVITATION_STATUS.PENDING || isInvitationExpired(invitation.expires_at)) {
    return Promise.resolve(
      errorEnvelope(409, 'INVITATION_NOT_PENDING', 'This invitation can no longer be accepted.')
    );
  }
  invitations = { ...invitations, [token]: { ...invitation, status: API_INVITATION_STATUS.ACCEPTED } };
  const [firstStudio] = invitation.studio_ids;
  return Promise.resolve(
    envelope({
      workspace_name: invitation.workspace_name,
      first_accessible_studio: firstStudio ? STUDIOS[firstStudio] : null,
    })
  );
};

/**
 * Send the invitation email to members (admin side, CT-5637). It goes out immediately and
 * cannot be recalled (only Resend or Revoke afterwards).
 * Mock of `POST /people-access/invitations` (contract TBD, ARP-1433).
 *
 * @param {string[]} memberIds
 * @returns {Promise<{ body: { sent: number, failed: string[] } }>}
 */
export const sendInvitations = async (memberIds) => {
  const results = await Promise.all(memberIds.map((id) => setMemberStatus(id, 'resend')));
  const failed = memberIds.filter((_, index) => results[index].status_code !== 200);
  return envelope({ sent: memberIds.length - failed.length, failed });
};
